import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { enrollmentService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Card, Badge, Button, Modal, Input, Select, Textarea, Field, EmptyState, Spinner, Pagination } from '../../components/ui';

const EMPTY_FORM = {
  method_type: 'bank_transfer', label: '', account_holder_name: '',
  account_number: '', bank_name: '', instructions: '', is_active: true, is_platform_default: true,
};

const METHOD_ICONS = { bank_transfer: '🏦', mobile_payment: '📱', cash: '💵', other: '💳' };
const PAGE_SIZE = 20;

function AdminPaymentAccounts() {
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const fetchAccounts = () => {
    setLoading(true);
    enrollmentService.getPaymentAccounts({ page, page_size: PAGE_SIZE })
      .then((res) => {
        const data = res.data;
        setAccounts(Array.isArray(data) ? data : data.results || []);
        setCount(Array.isArray(data) ? data.length : data.count || 0);
      })
      .catch(() => addNotification(t('Failed to load payment accounts'), 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchAccounts, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const defaults = accounts.filter((a) => a.is_platform_default);
  const teacherOwned = accounts.filter((a) => !a.is_platform_default);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a }); setModalOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const res = await enrollmentService.updatePaymentAccount(editing.id, form);
        setAccounts((prev) => prev.map((a) => (a.id === editing.id ? res.data : a)));
      } else {
        const res = await enrollmentService.createPaymentAccount(form);
        setAccounts((prev) => [...prev, res.data]);
        setCount((current) => current + 1);
      }
      addNotification(t('Payment account saved'), 'success');
      setModalOpen(false);
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to save payment account'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!(await confirm(t('Delete payment account "{{label}}"?', { label: a.label })))) return;
    try {
      await enrollmentService.deletePaymentAccount(a.id);
      setAccounts((prev) => prev.filter((x) => x.id !== a.id));
      setCount((current) => Math.max(0, current - 1));
      addNotification(t('Payment account deleted'), 'success');
    } catch (e) {
      addNotification(t('Failed to delete payment account'), 'error');
    }
  };

  const methodLabels = {
    bank_transfer: t('Bank Transfer'), mobile_payment: t('Mobile Payment'), cash: t('Cash'), other: t('Other'),
  };

  const renderCard = (a, editable) => (
    <Card key={a.id}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-semibold text-gray-900">{METHOD_ICONS[a.method_type]} {a.label}</p>
          <p className="text-xs text-gray-500">{methodLabels[a.method_type] || a.method_type}{a.owner_name && !a.is_platform_default ? ` · ${a.owner_name}` : ''}</p>
        </div>
        <Badge tone={a.is_active ? 'green' : 'gray'}>{a.is_active ? t('Active') : t('Inactive')}</Badge>
      </div>
      <div className="mt-3 space-y-1 text-sm text-gray-700">
        {a.account_holder_name && <p><span className="text-gray-500">{t('Holder')}:</span> {a.account_holder_name}</p>}
        {a.account_number && <p><span className="text-gray-500">{t('Account #')}:</span> {a.account_number}</p>}
        {a.bank_name && <p><span className="text-gray-500">{t('Bank')}:</span> {a.bank_name}</p>}
        {a.instructions && <p className="text-gray-500 italic mt-1">{a.instructions}</p>}
      </div>
      {editable && (
        <div className="flex gap-2 mt-4">
          <Button size="sm" variant="secondary" onClick={() => openEdit(a)}>{t('Edit')}</Button>
          <Button size="sm" variant="danger" onClick={() => remove(a)}>{t('Delete')}</Button>
        </div>
      )}
    </Card>
  );

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Payment Accounts')} subtitle={t('Platform-wide default accounts, shown when a course has none of its own')} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex justify-end mb-4">
            <Button onClick={openCreate}>+ {t('New Default Account')}</Button>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : (
            <>
              {defaults.length === 0 ? (
                <Card><EmptyState title={t('No platform default accounts yet')} description={t('Add one so courses without their own payment account still show students where to pay.')} action={<Button onClick={openCreate}>+ {t('New Default Account')}</Button>} /></Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {defaults.map((a) => renderCard(a, true))}
                </div>
              )}

              {teacherOwned.length > 0 && (
                <>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('Teacher-owned accounts (read-only here)')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teacherOwned.map((a) => renderCard(a, false))}
                  </div>
                </>
              )}
              <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} className="mt-4 rounded-xl border bg-white" />
            </>
          )}
        </main>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('Edit Payment Account') : t('New Default Payment Account')}
        footer={<>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('Cancel')}</Button>
          <Button loading={saving} onClick={save}>{t('Save')}</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label={t('Method')} required>
            <Select value={form.method_type} onChange={(e) => setForm({ ...form, method_type: e.target.value })}>
              <option value="bank_transfer">{t('Bank Transfer')}</option>
              <option value="mobile_payment">{t('Mobile Payment')}</option>
              <option value="cash">{t('Cash')}</option>
              <option value="other">{t('Other')}</option>
            </Select>
          </Field>
          <Field label={t('Label')} required hint={t('e.g. "Main School CCP Account"')}>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </Field>
          <Field label={t('Account Holder Name')}>
            <Input value={form.account_holder_name} onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })} />
          </Field>
          <Field label={t('Account Number')} hint={t('IBAN / RIP / CCP / phone number')}>
            <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} />
          </Field>
          <Field label={t('Bank Name')}>
            <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
          </Field>
          <Field label={t('Instructions')}>
            <Textarea rows={3} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            {t('Active (visible to students)')}
          </label>
        </div>
      </Modal>
    </div>
  );
}

export default AdminPaymentAccounts;
